'use client';

import PaginationWithPageSize from '@/components/PaginationWithPageSize';
import { DOMAIN_ADMIN } from '@/constaint/route';
import { useCountNewNotifications, useNotifications } from '@/hooks/useNotification';
import useQueryString from '@/hooks/useQueryString';
import { markAsRead } from '@/services/api/notification';
import { INotification } from '@/types/api/notification';
import {
  Badge,
  Divider,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollShadow,
} from '@nextui-org/react';
import { Bell } from '@phosphor-icons/react';
import moment from 'moment';
import Link from 'next/link';
import { useCallback, useState } from 'react';

const NotificationItem = ({ notification, onClose, refetch }: { notification: INotification, onClose: () => void, refetch: ()=>void }) => {
  const handleNotificationClick = useCallback(async (e: React.MouseEvent) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
      refetch();
    }
    
    const idMatch = notification.content.match(/ID:\s*(\d+)/i);

    if (idMatch && idMatch[1]) {
      const candidateId = idMatch[1]; 
           
      if (notification.url.includes('/job/')) {
        e.preventDefault();
        
        try {
          const fullUrl = notification.url.startsWith('http') ? notification.url : `http://example.com${notification.url}`;
          const urlObj = new URL(fullUrl);

          urlObj.searchParams.set('candidateId', candidateId);
          
          let newUrl = urlObj.pathname + urlObj.search;

          if (!notification.url.startsWith('http')) {
            newUrl = handleFormatUrl(newUrl);
          } else {
            newUrl = urlObj.toString();
          }
                    
          window.location.href = handleFormatUrl(newUrl);
          onClose();
          return;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error adding candidateId to URL:', error);
        }
      }
    }
    
    onClose();
  }, [notification, onClose, refetch]);

  const handleFormatUrl = (url: string) => {
    const localOrigin = location.origin;
    
    try {
      const isLocalhost = localOrigin?.includes("localhost");

      if (!isLocalhost) return url;
      if (!url || !url.trim()) return localOrigin; // Xử lý URL rỗng
      
      let fullUrl = url;

      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        fullUrl = url.startsWith('/') ? `${localOrigin}${url}` : `${localOrigin}/${url}`;
      }
      
      const urlObj = new URL(fullUrl);

      return localOrigin + urlObj.pathname + urlObj.search;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Invalid URL:', url);
      return localOrigin || '/'; 
    }
  };

  return (
    <Link
      href={handleFormatUrl(notification.url)}
      onClick={handleNotificationClick}
    >
      <div className='w-full text-primary font-medium cursor-pointer flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-[#cccccc1f]'>
        <div className='min-w-[8px] h-6 flex items-center'>
          <div
            className={`w-2 h-2 rounded-full ${notification.isRead ? 'bg-gray-300' : 'bg-blue-500'}`}
          ></div>
        </div>
        <div>
          <p className='text-sm font-bold'>{notification.title}</p>
          <p className='text-sm text-gray-500 limit-3-line'>{notification.content}</p>
          <p className='text-xs text-gray-400 mt-1'>{moment(notification.createdAt).fromNow()}</p>
        </div>
      </div>
    </Link>
  );
};

const MenuNotification = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { queryString, setQueryString } = useQueryString();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { data: notifications, isLoading, refetch } = useNotifications({ page, pageSize });
  const { data: newNotificationsCount, refetch: refetchCount } = useCountNewNotifications();

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleViewAll = () => {
    // Future implementation for viewing all notifications
    setIsOpen(false);
  };

  return (
    <Popover
      placement='bottom-end'
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      classNames={{
        content: 'p-0',
      }}
    >
      <PopoverTrigger>
        <div className='relative flex items-center cursor-pointer p-2 rounded-full hover:bg-gray-100'>
          <Badge
            content={newNotificationsCount}
            color='danger'
            shape='circle'
            showOutline={false}
            isInvisible={!newNotificationsCount || newNotificationsCount <= 0}
            classNames={{
              badge: 'text-tiny',
            }}
          >
            <Bell size={24} weight='regular' />
          </Badge>
        </div>
      </PopoverTrigger>
      <PopoverContent className='bg-white rounded-2xl border border-[#19052f0d] shadow-sm'>
        <ScrollShadow hideScrollBar className='max-h-[400px] max-w-[580px] w-full'>
          <div className='py-2'>
            {isLoading && <div className='p-4 text-center text-gray-500'>Loading...</div>}

            {!isLoading && notifications?.items?.length === 0 && (
              <div className='p-4 text-center text-gray-500'>No notifications</div>
            )}

            {!isLoading &&
              notifications?.items?.map((notification: INotification, index: number) => (
                <div key={notification.id}>
                  <NotificationItem 
                    notification={notification} 
                    onClose={handleClose} 
                    refetch={() => {
                      refetch();
                      refetchCount();
                    }}
                  />
                  {index < notifications.items.length - 1 && <Divider />}
                </div>
              ))}
          </div>
        </ScrollShadow>
        {notifications?.meta && (
          <div className='border-t border-gray-100'>
            <PaginationWithPageSize 
              size='sm' 
              meta={notifications.meta} 
              onChangePage={handleChangePage}
              onChangePageSize={handleChangePageSize}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MenuNotification;
